import { useState } from 'react';
import {
    Avatar,
    Box,
    Chip,
    Typography,
    IconButton,
    CircularProgress,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import { PeopleAltRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Player } from '../../../../types/models/Player';
import Colors from '../../../../styles/Colors';
import { SttDialog } from '../../../../sporttia/all';
import { BookingCreator } from '../../../../types/models/Game';

interface PlayerListProps {
    slots: (Player | null)[];
    onDeletePlayer: (player: Player) => void;
    canDeletePlayer?: (player: Player) => boolean;
    deletingPlayerId?: number | null;
    creator: BookingCreator | null;
}

export default function PlayerList({
    slots,
    onDeletePlayer,
    canDeletePlayer,
    deletingPlayerId,
    creator,
}: PlayerListProps) {
    const { t } = useTranslation();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);

    const handleDeleteClick = (player: Player) => {
        setSelectedPlayer(player);
        setConfirmOpen(true);
    };

    const handleConfirmDelete = () => {
        if (selectedPlayer && typeof onDeletePlayer === 'function') {
            onDeletePlayer(selectedPlayer);
        }
        setConfirmOpen(false);
        setSelectedPlayer(null);
    };

    return (
        <Box>
            <Box display="flex" columnGap={10} alignItems="center" mb={2}>
                <PeopleAltRounded fontSize="medium" />
                <Typography
                    variant="h6"
                    align="center"
                    style={{
                        fontWeight: 800,
                        textAlign: 'center',
                    }}
                >
                    {t('matchPlayers')}
                </Typography>
            </Box>

            {slots.map((player, idx) => (
                <Box
                    key={player?.id || idx}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 16px',
                        marginBottom: 12,
                        background: player ? Colors.MG1 : 'transparent',
                        borderRadius: 8,
                        borderWidth: player ? 0 : 1,
                        borderStyle: 'dashed',
                        borderColor: Colors.G2,
                        position: 'relative',
                    }}
                >
                    <Avatar
                        style={{
                            width: 48,
                            height: 48,
                            backgroundColor: player ? Colors.MG5 : Colors.G1,
                            color: player ? '#FFF' : Colors.G3,
                            fontSize: 22,
                            marginRight: 16,
                        }}
                    >
                        {player ? (
                            (player.user.name || '?')
                                .split(' ')
                                .map((n: any) => n[0])
                                .join('')
                        ) : (
                            <PersonAddIcon fontSize="medium" />
                        )}
                    </Avatar>
                    <Box flex={1}>
                        <Box display="flex" alignItems="center">
                            {player ? (
                                <Typography
                                    variant="body1"
                                    style={{
                                        fontWeight: 600,
                                        color: Colors.MG5,
                                        fontSize: 18,
                                    }}
                                >
                                    {player.user.fullName || player.user.name}
                                </Typography>
                            ) : (
                                <Typography
                                    variant="body1"
                                    style={{
                                        fontWeight: 600,
                                        color: Colors.G3,
                                        fontSize: 18,
                                    }}
                                >
                                    {t('const.TT.FREE')}
                                </Typography>
                            )}
                            {player && player.user.id === creator?.id && (
                                <Chip
                                    label={t('Creator')}
                                    size="small"
                                    style={{
                                        marginLeft: 8,
                                        backgroundColor: Colors.MG5,
                                        fontWeight: 600,
                                        color: '#FFF',
                                        fontSize: 12,
                                    }}
                                />
                            )}
                        </Box>
                        <Box display="flex" alignItems="center" mt={0.5}>
                            {player && player.level && (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    style={{ marginRight: 12 }}
                                >
                                    {t('Level')}: {player.level}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    {player && canDeletePlayer?.(player) && (
                        <IconButton
                            aria-label="delete"
                            onClick={() => handleDeleteClick(player)}
                            style={{ marginLeft: 'auto' }}
                            disabled={deletingPlayerId === player.id}
                        >
                            {deletingPlayerId === player.id ? (
                                <CircularProgress size={24} />
                            ) : (
                                <DeleteIcon />
                            )}
                        </IconButton>
                    )}
                </Box>
            ))}
            <SttDialog
                open={confirmOpen}
                title={t('DeletePlayer')}
                maxWidth="xs"
                onClose={() => setConfirmOpen(false)}
                content={<Typography>{t('AreYouSureDeletePlayer')}</Typography>}
                buttons={[
                    {
                        type: 'close',
                        onClick: () => setConfirmOpen(false),
                        caption: t('cancel'),
                    },
                    {
                        type: 'delete',
                        onClick: handleConfirmDelete,
                        caption: t('delete'),
                        color: 'secondary',
                    },
                ]}
            />
        </Box>
    );
}
